/**
 * Revenue Page
 *
 * MRR, ARR, subscription tier distribution, and revenue trends.
 * Shows financial metrics for the platform.
 */

import { useState, useEffect } from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import DistributionChart from '../../components/founder-dashboard/DistributionChart';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  CurrencyDollarIcon,
  SparklesIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';

export default function RevenuePage() {
  const { currentEnv } = useEnvironment();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null); // Track which env the data/error belongs to

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getRevenue();
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[RevenuePage] Error fetching data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Clear all state and refetch when environment changes
    setData(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);

  // Only show error if it's from the current environment
  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading revenue data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const metrics = data?.metrics || {};
  const subscriptionsByTier = data?.subscriptionsByTier || {};
  const revenueByTier = data?.revenueByTier || {};
  const billingPeriodDistribution = data?.billingPeriodDistribution || {};
  const trials = data?.trials || {};
  const revenueByMonth = data?.revenueByMonth || [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Revenue Metrics</h2>
          <p className="text-gray-500 mt-1">
            MRR, ARR, and subscription analytics
          </p>
        </div>
        <RefreshIndicator lastUpdated={data?.timestamp} onRefresh={fetchData} loading={loading} />
      </div>

      {/* Revenue KPIs */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CurrencyDollarIcon className="w-6 h-6 text-green-500" />
          Revenue Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <KPICard
            title="Monthly Recurring Revenue"
            value={metrics.mrr}
            format="currency"
            loading={loading}
          />
          <KPICard
            title="Annual Recurring Revenue"
            value={metrics.arr}
            format="currency"
            loading={loading}
          />
        </div>
      </section>

      {/* MRR Trend */}
      {revenueByMonth.length > 0 && (
        <section>
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            MRR Trend (Last 6 Months)
          </h3>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {loading ? (
              <div className="h-64 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <TrendChart
                data={revenueByMonth.map((r) => ({
                  date: r.month,
                  mrr: r.mrr,
                }))}
                lines={[{ key: 'mrr', name: 'MRR', color: '#10B981' }]}
                xAxisKey="date"
                height={250}
              />
            )}
          </div>
        </section>
      )}

      {/* Subscriptions by Tier */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <SparklesIcon className="w-6 h-6 text-purple-500" />
          Subscription Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Subscriptions by Tier Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Subscriptions by Tier</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <DistributionChart data={subscriptionsByTier} height={200} />
            )}
          </div>

          {/* Revenue by Tier Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Revenue by Tier</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <DistributionChart data={revenueByTier} height={200} />
            )}
          </div>
        </div>

        {/* Tier Breakdown Table */}
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscriptions
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Revenue (MRR)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4">
                    <div className="h-8 animate-pulse bg-gray-100 rounded"></div>
                  </td>
                </tr>
              ) : (
                Object.keys(subscriptionsByTier).map((tier) => (
                  <tr key={tier} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900 capitalize">{tier}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-700">
                      {subscriptionsByTier[tier] || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-gray-700">
                      {formatCurrency(revenueByTier[tier] || 0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Billing Period Distribution */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CalendarIcon className="w-6 h-6 text-blue-500" />
          Billing Distribution
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Monthly vs Annual</h4>
            {loading ? (
              <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
            ) : (
              <DistributionChart data={billingPeriodDistribution} height={200} />
            )}
          </div>

          {/* Billing Period Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-medium text-gray-700 mb-4">Billing Breakdown</h4>
            {loading ? (
              <div className="space-y-4">
                {[1, 2].map((i) => (
                  <div key={i} className="h-12 animate-pulse bg-gray-100 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-700">Monthly Billing</span>
                  <span className="text-xl font-bold text-gray-900">
                    {billingPeriodDistribution.monthly || 0}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-gray-700">Annual Billing</span>
                  <span className="text-xl font-bold text-gray-900">
                    {billingPeriodDistribution.annual || 0}
                  </span>
                </div>
                {billingPeriodDistribution.unknown > 0 && (
                  <div className="flex items-center justify-between py-3">
                    <span className="text-gray-500">Unknown</span>
                    <span className="text-xl font-bold text-gray-500">
                      {billingPeriodDistribution.unknown}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Trial Metrics */}
      <section>
        <h3 className="text-xl font-semibold text-gray-800 mb-4">
          Trial Metrics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KPICard
            title="Active Trials"
            value={trials.activeTrials}
            loading={loading}
          />
          <KPICard
            title="Expired Trials"
            value={trials.expiredTrials}
            loading={loading}
          />
          <KPICard
            title="Converted (Last 30d)"
            value={trials.convertedLast30Days}
            loading={loading}
            subtitle="Trials that became paid"
          />
        </div>
      </section>
    </div>
  );
}
